import { ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, forwardRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { NetworkConstants } from 'diwimamodelweb/lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SettingsService } from 'src/app/shared/settings.service';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

export type ContextRight = 'view' | 'update';

/** Legacy spelling of `update`, present in documents written before the rename. */
export const CONTEXT_RIGHT_UPDATE_LEGACY = 'edit';
export type ContextIdRights = Record<string, ContextRight>;


export function contextIdsFromRights(rights: ContextIdRights | null | undefined): string[] {
  return Object.keys(rights ?? {});
}

export function applySharingFields(
  target: any,
  rights: ContextIdRights | null | undefined,
  hadSharing: boolean,
): void {
  if (!target) return;
  const ids = contextIdsFromRights(rights);

  if (ids.length) {
    target.contextIdRights = rights;
    target.contextIds = ids;
    return;
  }

  if (hadSharing) {
    target.contextIdRights = null;
    target.contextIds = null;
    return;
  }

  delete target.contextIdRights;
  delete target.contextIds;
}

interface ContextRow {
  id: string;
  name: string;
  shared: boolean;
  right: ContextRight;
}

@Component({
  selector: 'app-context-rights-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './context-rights-picker.component.html',
  styleUrls: ['./context-rights-picker.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => ContextRightsPickerComponent),
    multi: true,
  }],
})
export class ContextRightsPickerComponent implements OnInit, OnDestroy, ControlValueAccessor {

  @Input() label = this.translate.instant('context_sharing');
  @Input() expanded = false;

  readonly RIGHTS: { value: ContextRight; label: string; icon: string }[] = [
    { value: 'view', label: this.translate.instant('view'),    icon: 'visibility' },
    { value: 'update', label: this.translate.instant('edit'), icon: 'edit' },
  ];

  private firestore = inject(Firestore);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  rows: ContextRow[] = [];
  isSuperContext = false;
  disabled = false;
  loading = false;
  filterText = '';

  private value: ContextIdRights = {};
  private nameCache = new Map<string, string>();
  private destroy$ = new Subject<void>();

  private onChange: (v: ContextIdRights) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private settingsService: SettingsService,
    private translate: TranslateService) {}

  ngOnInit(): void {
    this.settingsService.selectedUserContext$
      .pipe(takeUntil(this.destroy$))
      .subscribe(ctx => this.loadContextConfig(ctx?.identifier));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: ContextIdRights | null): void {
    this.value = value ?? {};
    this.syncRowsFromValue();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: ContextIdRights) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }



  /**
   * `superContext` / `subContextIds` are read straight from the Context document
   * rather than from `selectedUserContext$`: the hydrated Context instance does
   * not carry them (setFromAny only copies its own known fields).
   */
  private async loadContextConfig(contextId?: string): Promise<void> {
    if (!contextId) {
      this.zone.run(() => {
        this.isSuperContext = false;
        this.rows = [];
        this.cdr.markForCheck();
      });
      return;
    }

    this.zone.run(() => { this.loading = true; this.cdr.markForCheck(); });

    let data: any = {};
    try {
      const snap = await getDoc(doc(this.firestore, NetworkConstants.COLLECTION_CONTEXT, contextId));
      data = snap.data() ?? {};
    } catch {
      data = {};
    }

    // Sub-contexts only — the active (super) context owns the document anyway.
    const selectedSubContextIds = this.settingsService.selectedContextAndSubContextIds.value ?? []
    const ids: string[] = (Array.isArray(data.subContextIds) ? data.subContextIds : [])

    this.zone.run(() => {
      this.isSuperContext = !!data.superContext;
      this.rows = this.isSuperContext
        ? ids.map(id => ({
            id,
            name: this.nameCache.get(id) ?? id,
            shared: false,
            right: 'view' as ContextRight,
          }))
        : [];
      this.syncRowsFromValue();
      this.loading = false;
      this.cdr.markForCheck();
    });

    if (this.isSuperContext) this.resolveNames(ids);
  }

  private resolveNames(ids: string[]): void {
    const missing = ids.filter(id => id && !this.nameCache.has(id));
    if (!missing.length) return;

    Promise.all(missing.map(async id => {
      try {
        const snap = await getDoc(doc(this.firestore, NetworkConstants.COLLECTION_CONTEXT, id));
        if (!snap.exists()) {
          console.warn(`[ContextRightsPicker] Context/${id} does not exist`);
          return [id, null] as const;
        }
        const name = (snap.data() as any)?.name;
        if (!name) {
          console.warn(`[ContextRightsPicker] Context/${id} has no name. Keys:`, Object.keys(snap.data() ?? {}));
        }
        return [id, name ?? null] as const;
      } catch (err) {
        console.error(`[ContextRightsPicker] Failed to read Context/${id}:`, err);
        return [id, null] as const;
      }
    })).then(results => {
      results.forEach(([id, name]) => { if (name) this.nameCache.set(id, name); });
      this.zone.run(() => {
        this.rows = this.rows.map(r => ({ ...r, name: this.nameCache.get(r.id) ?? r.id }));
        this.cdr.markForCheck();
      });
    });
  }

  private syncRowsFromValue(): void {
    this.rows = this.rows.map(r => ({
      ...r,
      shared: this.value?.[r.id] != null,
      right: this.value?.[r.id] ?? 'view',
    }));
  }

  get visibleRows(): ContextRow[] {
    const q = this.filterText.trim().toLowerCase();
    const filtered = q ? this.rows.filter(r => r.name.toLowerCase().includes(q)) : this.rows;
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }

  get sharedCount(): number {
    return this.rows.filter(r => r.shared).length;
  }

  get sharedVisibleCount(): number {
    return this.visibleRows.filter(r => r.shared).length;
  }

  get allVisibleShared(): boolean {
    const visible = this.visibleRows;
    return visible.length > 0 && visible.every(r => r.shared);
  }

  get someVisibleShared(): boolean {
    return this.visibleRows.some(r => r.shared) && !this.allVisibleShared;
  }


  onFilterChanged(value: string): void {
    this.filterText = value;
  }

  toggleShared(row: ContextRow, shared: boolean): void {
    if (this.disabled) return;
    row.shared = shared;
    this.emit();
  }

  toggleAllVisible(shared: boolean): void {
    if (this.disabled) return;
    const visible = new Set(this.visibleRows.map(r => r.id));
    this.rows.forEach(r => { if (visible.has(r.id)) r.shared = shared; });
    this.emit();
  }

  setRight(row: ContextRow, right: ContextRight): void {
    if (this.disabled || !right) return;
    row.right = right;
    this.emit();
  }

  applyRight(right: ContextRight): void {
    if (this.disabled || !this.sharedVisibleCount) return;
    const visible = new Set(this.visibleRows.map(r => r.id));
    this.rows.forEach(r => { if (r.shared && visible.has(r.id)) r.right = right; });
    this.emit();
  }

  trackById = (_: number, row: ContextRow) => row.id;

  private emit(): void {
    const out: ContextIdRights = {};
    this.rows.forEach(r => { if (r.shared) out[r.id] = r.right; });
    this.value = out;
    this.onChange(out);
    this.onTouched();
  }
}